import { useCallback, useState, type JSX } from 'react';

import Select, { components, OptionProps } from 'react-select';
import { CheckmarkIcon } from '@ag1/ui';
import { OptionType } from '../Select/Select.types';
import { GetTargetButtonProps, PopoutDropdownProps, PopoutProps } from './Popout.types';
import callAll from '../../utils/callAll';

const Blanket = (props: JSX.IntrinsicElements['div']) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      zIndex: 1
    }}
    {...props}
  />
);

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
  fullWidth,
  position,
  borderRadius = '6px',
  className,
  alignLeft
}: PopoutDropdownProps) => (
  <div style={{ position: 'relative' }}>
    {target}
    {isOpen ? (
      <div
        style={{
          boxShadow: '0px 4px 8px rgb(107 114 128 / 16%)',
          borderRadius,
          overflow: 'hidden',
          backgroundColor: 'white',
          position: 'absolute',
          zIndex: 2,
          minWidth: fullWidth ? '100%' : '240px',
          marginTop: '4px',
          marginBottom: '4px',
          top: position === 'bottom' ? '100%' : 'auto',
          bottom: position === 'top' ? '100%' : 'auto',
          right: alignLeft ? '0' : 'auto'
        }}
        className={className}>
        {children}
      </div>
    ) : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const CheckboxOption = (props: OptionProps) => {
  const { label, isSelected } = props;

  return (
    <components.Option {...props}>
      {label}
      {isSelected && <CheckmarkIcon size={12} />}
    </components.Option>
  );
};

export const Popout = ({
  options,
  renderAddon,
  value,
  onChange,
  fullWidth,
  alignLeft,
  wrapperClassName,
  className,
  optionClassName,
  borderRadius = '6px',
  position = 'bottom'
}: PopoutProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = useCallback(
    (newValue: OptionType | null) => {
      if (onChange) {
        onChange(newValue);
      }
      setIsOpen(false);
    },
    [onChange]
  );

  const getTargetButtonProps: GetTargetButtonProps = useCallback(
    ({ onClick, ...restProps } = {}) => ({
      ...restProps,
      onClick: callAll(onClick, () => {
        setIsOpen((prev) => !prev);
      })
    }),
    []
  );

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      fullWidth={fullWidth}
      position={position}
      alignLeft={alignLeft}
      borderRadius={borderRadius}
      className={wrapperClassName}
      target={renderAddon?.({ getTargetButtonProps, isOpen })}>
      <Select
        value={value}
        options={options}
        onChange={handleChange}
        autoFocus
        className={className}
        classNames={{
          option: (baseProps) => {
            const defaultClass = baseProps.className || '';
            return optionClassName ? `${defaultClass} ${optionClassName}` : defaultClass;
          }
        }}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen
        components={{
          Option: CheckboxOption
        }}
        isSearchable={false}
        styles={{
          control: () => ({
            display: 'none'
          }),
          menu: () => ({
            minWidth: '100%'
          }),
          menuList: () => ({
            overflowY: 'auto',
            maxHeight: 280
          }),
          option: (baseStyles, { isSelected }) => ({
            ...baseStyles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: isSelected ? 700 : 400,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#292B29',
            padding: '12px 16px',
            cursor: 'pointer',
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#F7F9F9'
            }
          })
        }}
        tabSelectsValue={false}
      />
    </Dropdown>
  );
};
